import React, { useEffect, useState } from 'react';
import { Button, Card, CardContent, CircularProgress, Grid, Tooltip, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { SCUIGeneralDrawer } from '../../../../../../atomic/molecules';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { useTranslation } from 'react-i18next';
import UpdateSaleOrderAddress from './components/UpdateSaleOrderAddress';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import UpdateBillingAdress from './components/UpdateBillingAdress';
import Swal from 'sweetalert2';
import { AddressEntityType } from '../../../../../../types/enums/addressEntityType-enum';
import { DeliveryMethodEnum } from '../../../../../../types/enums';
import { AIC, aicCls } from './addressInformationCard.classes';
import {
    fetchCountryAddressRules,
    fetchMobileCountryAddressRules,
    mapOrderRecipientToAddressModels,
} from './addressInformationCard.helpers';

function AddressInformationCard({
    orderId,
    orderInformation,
    onRefreshOrderInformation,
}: {
    orderId: any;
    orderInformation?: any;
    onRefreshOrderInformation?: () => void;
}) {
    const { t } = useTranslation();
    const [data, setData] = useState<any>();
    const [loading, setLoading] = useState<any>(true);
    const [addressDetail, setAddressDetail] = useState<any>();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [shippingAddressDrawerOpen, setShippingAddressDrawerOpen] = useState(false);
    const [billingAddressDetail, setBillingAddressDetail] = useState<any>();
    const [addressEntityTypeListByCountryId, setAddressEntityTypeListByCountryId] = useState<any>();
    const [mobileAddressRules, setMobileAddressRules] = useState<any[]>([]);
    const [deliveryMethodId, setDeliveryMethodId] = useState<number | null>(null);
    const [isBillingAddressHovered, setIsBillingAddressHovered] = useState(false);
    const [isShippingAddressHovered, setIsShippingAddressHovered] = useState(false);

    const handleRefresh = () => {
        onRefreshOrderInformation?.();
    };

    const onDrawerClose = () => setDrawerOpen(false);
    const onDrawerShippingAddressClose = () => setShippingAddressDrawerOpen(false);
    const handleAddressDrawerOpen = (detail: any) => {
        if (!detail.isShippingAddressChangeable) {
            return; //adres değiştirilebilir değilse devam etmesin
        }
        setDrawerOpen(true);
        setAddressDetail({
            ...detail,
            customerId: orderId.customerId,
            orderId: orderId.orderId,
            orderNumber: orderId.orderNumber,
            billingAddressId:
                data?.saleOrderBillingAddressModel?.customerAddressId ??
                data?.saleOrderBillingAddressModel?.saleOrderBillingAddressId ??
                data?.saleOrderBillingAddressModel?.billingAddressId,
            shippingAddressId:
                detail.customerAddressId ?? detail.saleOrderShippingAddressId ?? detail.shippingAddressId,
        });
    };
    const handleShippingAddressDrawerOpen = (detail: any) => {
        if (!detail.isBillingAddressChangeable) {
            return;
        }
        setShippingAddressDrawerOpen(true);
        setBillingAddressDetail({
            ...detail,
            customerId: orderId.customerId,
            orderId: orderId.orderId,
            orderNumber: orderId.orderNumber,
            billingAddressId: detail.customerAddressId ?? detail.saleOrderBillingAddressId ?? detail.billingAddressId,
            shippingAddressId:
                data?.saleOrderShippingAddressModel?.customerAddressId ??
                data?.saleOrderShippingAddressModel?.saleOrderShippingAddressId ??
                data?.saleOrderShippingAddressModel?.shippingAddressId,
        });
    };

    const applyOrderInformation = async (info: any) => {
        try {
            const addressData = mapOrderRecipientToAddressModels(info);
            setData(addressData);
            const deliveryMethodIdFromAddress = addressData?.saleOrderShippingAddressModel?.deliveryMethodId;
            const parsedDeliveryMethodId =
                deliveryMethodIdFromAddress != null ? Number(deliveryMethodIdFromAddress) : null;
            setDeliveryMethodId(parsedDeliveryMethodId);
            if (parsedDeliveryMethodId != null) {
                try {
                    const rules = await fetchMobileCountryAddressRules(parsedDeliveryMethodId);
                    setMobileAddressRules(rules);
                } catch (error: any) {
                    setMobileAddressRules([]);
                    Swal.fire('Error', error.toString(), 'error');
                }
            } else {
                setMobileAddressRules([]);
            }
        } catch {
            // keep existing behavior: silently stop loading on failure
        } finally {
            setLoading(false);
        }
    };
    const CountryAddressRulesforCountryId = async () => {
        try {
            const rules = await fetchCountryAddressRules();
            setAddressEntityTypeListByCountryId(rules);
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        }
    };

    const addressEntityFieldMap: Record<AddressEntityType, string> = {
        [AddressEntityType.Region]: 'countryZoneName',
        [AddressEntityType.District]: 'districtName',
        [AddressEntityType.City]: 'cityName',
        [AddressEntityType.County]: 'countyName',
        [AddressEntityType.PostalCode]: 'postalCode',
        [AddressEntityType.Street]: 'streetName',
        [AddressEntityType.House]: 'house',
        [AddressEntityType.Floor]: 'floorName',
        [AddressEntityType.Flat]: 'flatName',
        [AddressEntityType.Neighborhood]: 'neighborhoodName',
        [AddressEntityType.PersonalNumber]: 'personalNumber',
    };
    const addressEntityLabels: Record<AddressEntityType, string> = {
        [AddressEntityType.Region]: t('addressDetail.Region'),
        [AddressEntityType.District]: t('addressDetail.District'),
        [AddressEntityType.City]: t('addressDetail.City'),
        [AddressEntityType.County]: t('addressDetail.County'),
        [AddressEntityType.PostalCode]: t('addressDetail.PostalCode'),
        [AddressEntityType.Street]: t('addressDetail.streetName'),
        [AddressEntityType.House]: t('addressDetail.House'),
        [AddressEntityType.Floor]: t('addressDetail.Floor'),
        [AddressEntityType.Flat]: t('addressDetail.Flat'),
        [AddressEntityType.Neighborhood]: t('addressDetail.Neighborhood'),
        [AddressEntityType.PersonalNumber]: t('addressDetail.PersonalNumber'),
    };

    const renderSaleOrderBillingAddressDetails = () => {
        if (!addressEntityTypeListByCountryId) return null;
        return (
            <Grid className={AIC.billingEntityGrid} container spacing={1} sx={{ mt: 0.5 }}>
                {addressEntityTypeListByCountryId.map((rule: any) => {
                    const type = rule.addressEntityTypeId as AddressEntityType;
                    const field = addressEntityFieldMap[type];
                    const label = addressEntityLabels[type];
                    const val = data.saleOrderBillingAddressModel[field];
                    return (
                        <React.Fragment key={type}>
                            <Grid className={AIC.entityFieldLabel} size={6}>
                                <p style={{ fontWeight: 'bold' }}>{label} :</p>
                            </Grid>
                            <Grid className={AIC.entityFieldValue} size={6}>
                                <p>{val ?? '-'}</p>
                            </Grid>
                        </React.Fragment>
                    );
                })}
            </Grid>
        );
    };
    const renderSaleOrderShippingAddressDetails = () => {
        if (!addressEntityTypeListByCountryId) return null;
        return (
            <Grid className={AIC.shippingEntityGrid} container spacing={1} sx={{ mt: 0.5 }}>
                {addressEntityTypeListByCountryId.map((rule: any) => {
                    const type = rule.addressEntityTypeId as AddressEntityType;
                    const field = addressEntityFieldMap[type];
                    const label = addressEntityLabels[type];
                    const val = data.saleOrderShippingAddressModel[field];

                    return (
                        <React.Fragment key={type}>
                            <Grid className={AIC.entityFieldLabel} size={6}>
                                <p style={{ fontWeight: 'bold' }}>{label} :</p>
                            </Grid>
                            <Grid className={AIC.entityFieldValue} size={6}>
                                <p>{val ?? '-'}</p>
                            </Grid>
                        </React.Fragment>
                    );
                })}
            </Grid>
        );
    };
    useEffect(() => {
        if (orderInformation === undefined) {
            return;
        }
        applyOrderInformation(orderInformation);
    }, [orderInformation]);

    useEffect(() => {
        CountryAddressRulesforCountryId();
    }, []);

    return (
        <>
            <SCUIGeneralDrawer
                open={drawerOpen}
                width={'350px'}
                paperClassName={AIC.drawerShippingPaper}
                onClose={(event: any, reason: any) => {
                    if (reason === 'backdropClick') {
                        onDrawerClose();
                    }
                }}
            >
                <UpdateSaleOrderAddress
                    detail={addressDetail}
                    onClose={onDrawerClose}
                    setRefresh={handleRefresh}
                    addressRules={mobileAddressRules}
                    deliveryMethodId={deliveryMethodId}
                />
            </SCUIGeneralDrawer>
            <SCUIGeneralDrawer
                open={shippingAddressDrawerOpen}
                width={'350px'}
                paperClassName={AIC.drawerBillingPaper}
                onClose={(event: any, reason: any) => {
                    if (reason === 'backdropClick') {
                        onDrawerShippingAddressClose();
                    }
                }}
            >
                <UpdateBillingAdress
                    detail={billingAddressDetail}
                    onClose={onDrawerShippingAddressClose}
                    setRefresh={handleRefresh}
                    addressRules={mobileAddressRules}
                    deliveryMethodId={deliveryMethodId}
                />
            </SCUIGeneralDrawer>

            {loading ? (
                <Grid
                    className={AIC.loadingGrid}
                    container
                    style={{ height: '100%' }}
                    alignItems="center"
                    justifyContent="center"
                >
                    <div
                        className={AIC.loadingInner}
                        style={{
                            textAlign: 'center',
                            position: 'relative',
                            pointerEvents: 'none',
                        }}
                    >
                        <CircularProgress size={80} />
                    </div>
                </Grid>
            ) : (
                <Grid
                    size={{ xs: 12, sm: 12, md: 12 }}
                    sx={{ boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)' }}
                    className={aicCls('addressInfos', AIC.root)}
                >
                    <Card className={AIC.headerCard} sx={{ padding: '1rem', display: 'flex' }}>
                        <LocationOnIcon className={AIC.headerIcon} style={{ marginTop: '0.5rem' }} />
                        <h3 className={aicCls('heading-md', AIC.title)} style={{ marginLeft: '0.5rem' }}>
                            <strong> {t('orderDetail.adressInfos')}</strong>
                        </h3>
                    </Card>
                    <div className={AIC.divider} style={{ height: '2px', background: 'lightGray' }}></div>
                    <Card className={AIC.bodyCard} sx={{ height: '180px' }}>
                        <CardContent className={AIC.bodyContent}>
                            {data ? (
                                <Grid className={AIC.addressesRow} container style={{ display: 'flex' }}>
                                    <Grid
                                        className={AIC.billingColumn}
                                        size={{ xs: 6, md: 6, lg: 6 }}
                                        style={{ alignItems: 'center' }}
                                    >
                                        <div className={AIC.billingHeaderRow} style={{ display: 'flex', gap: '1rem' }}>
                                            {data?.saleOrderBillingAddressModel && (
                                                <>
                                                    {data.saleOrderBillingAddressModel.taxOffice ? (
                                                        <Tooltip title={<div>{t('addressDetail.corporate')}</div>}>
                                                            <BusinessCenterIcon className={AIC.billingTypeIconWrap} />
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title={<div>{t('addressDetail.individual')}</div>}>
                                                            <PermContactCalendarIcon
                                                                className={AIC.billingTypeIconWrap}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                    <h6 className={aicCls('heading-sm', AIC.billingHeading)}>
                                                        {t('orderDetail.billingAdress')}
                                                    </h6>
                                                    <Button
                                                        className={AIC.inputButtonEditBilling}
                                                        disabled={
                                                            !data.saleOrderBillingAddressModel
                                                                .isBillingAddressChangeable
                                                        }
                                                        onClick={() =>
                                                            handleShippingAddressDrawerOpen(
                                                                data.saleOrderBillingAddressModel
                                                            )
                                                        }
                                                    >
                                                        <EditNoteIcon
                                                            style={{
                                                                cursor: 'pointer',
                                                                color: data.saleOrderBillingAddressModel
                                                                    .isBillingAddressChangeable
                                                                    ? '#5b99f3'
                                                                    : 'gray',
                                                            }}
                                                        />
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                        <div
                                            className={aicCls('text-sm', AIC.billingAddressBody)}
                                            onMouseEnter={() => setIsBillingAddressHovered(true)}
                                            onMouseLeave={() => setIsBillingAddressHovered(false)}
                                            style={{
                                                maxHeight: '100px',
                                                overflowY: isBillingAddressHovered ? 'auto' : 'hidden',
                                                fontSize: '11px',
                                                transition: 'all 0.2s ease-in-out',
                                                scrollbarWidth: 'thin',
                                            }}
                                        >
                                            {data?.saleOrderBillingAddressModel && (
                                                <>
                                                    <div
                                                        className={AIC.billingSummaryLine}
                                                        style={{
                                                            display: 'flex',
                                                            flexWrap: 'wrap',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                            margin: '4px 0 8px 0',
                                                        }}
                                                    >
                                                        <span>
                                                            {data.saleOrderBillingAddressModel.firstName +
                                                                ' ' +
                                                                data.saleOrderBillingAddressModel.lastName}
                                                        </span>
                                                        <span style={{ margin: '0 4px' }}>|</span>
                                                        <span>{data.saleOrderBillingAddressModel.address}</span>
                                                        <span style={{ margin: '0 4px' }}>|</span>
                                                        <span>
                                                            {(
                                                                data.saleOrderBillingAddressModel
                                                                    ?.combinedPhoneNumber ?? ''
                                                            ).trim()}
                                                        </span>
                                                    </div>
                                                    {typeof data.saleOrderBillingAddressModel
                                                        .externalAddressDefinition === 'string' &&
                                                        data.saleOrderBillingAddressModel.externalAddressDefinition.trim() && (
                                                            <p>
                                                                <strong>
                                                                    {t('orderDetail.externalAddressDefinition')}:
                                                                </strong>{' '}
                                                                {
                                                                    data.saleOrderBillingAddressModel
                                                                        .externalAddressDefinition
                                                                }
                                                            </p>
                                                        )}
                                                    <p>
                                                        {(
                                                            data.saleOrderBillingAddressModel?.combinedPhoneNumber ?? ''
                                                        ).trim()}
                                                    </p>
                                                    {Number(data.saleOrderShippingAddressModel?.deliveryMethodId) !==
                                                        DeliveryMethodEnum.PickPoint &&
                                                        data.saleOrderBillingAddressModel.externalReferanceCode && (
                                                            <p>
                                                                {
                                                                    data.saleOrderBillingAddressModel
                                                                        .externalReferanceCode
                                                                }
                                                            </p>
                                                        )}
                                                    {data.saleOrderBillingAddressModel.taxOffice &&
                                                        data.saleOrderBillingAddressModel.taxNumber && (
                                                            <>
                                                                <p style={{ marginTop: '5px' }}>
                                                                    <strong>{t('addressDetail.taxOffice')} : </strong>
                                                                    {data.saleOrderBillingAddressModel.taxOffice}
                                                                </p>
                                                                <p>
                                                                    <strong>{t('addressDetail.taxNumber')} : </strong>
                                                                    {data.saleOrderBillingAddressModel.taxNumber}
                                                                </p>
                                                            </>
                                                        )}
                                                    {data.saleOrderBillingAddressModel.postalCode && (
                                                        <p>{data.saleOrderBillingAddressModel.postalCode}</p>
                                                    )}
                                                    <div>{renderSaleOrderBillingAddressDetails()}</div>
                                                </>
                                            )}
                                        </div>
                                    </Grid>
                                    <Grid
                                        className={AIC.shippingColumn}
                                        size={{ xs: 6, md: 6, lg: 6 }}
                                        style={{ justifyContent: 'center' }}
                                    >
                                        <div className={AIC.shippingHeaderRow} style={{ display: 'flex', gap: '1rem' }}>
                                            {data?.saleOrderShippingAddressModel && (
                                                <>
                                                    <LocalShippingIcon className={AIC.shippingIcon} />
                                                    <h6
                                                        className={aicCls('heading-sm', AIC.shippingHeading)}
                                                        style={{ minWidth: '90px' }}
                                                    >
                                                        {t('orderDetail.deliveryAdress')}
                                                    </h6>
                                                    <Button
                                                        className={AIC.inputButtonEditShipping}
                                                        disabled={
                                                            !data.saleOrderShippingAddressModel
                                                                .isShippingAddressChangeable
                                                        }
                                                        onClick={() =>
                                                            handleAddressDrawerOpen(data.saleOrderShippingAddressModel)
                                                        }
                                                    >
                                                        <EditNoteIcon
                                                            style={{
                                                                cursor: 'pointer',
                                                                color: data.saleOrderShippingAddressModel
                                                                    .isShippingAddressChangeable
                                                                    ? '#5b99f3'
                                                                    : 'gray',
                                                            }}
                                                        />
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                        <div
                                            className={aicCls('text-sm', AIC.shippingAddressBody)}
                                            onMouseEnter={() => setIsShippingAddressHovered(true)}
                                            onMouseLeave={() => setIsShippingAddressHovered(false)}
                                            style={{
                                                maxHeight: '100px',
                                                overflowY: isShippingAddressHovered ? 'auto' : 'hidden',
                                                fontSize: '11px',
                                                transition: 'all 0.2s ease-in-out',
                                                scrollbarWidth: 'thin',
                                            }}
                                        >
                                            {data?.saleOrderShippingAddressModel && (
                                                <>
                                                    <div
                                                        className={AIC.shippingSummaryLine}
                                                        style={{
                                                            display: 'flex',
                                                            flexWrap: 'wrap',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                            margin: '4px 0 8px 0',
                                                        }}
                                                    >
                                                        <span>
                                                            {data.saleOrderShippingAddressModel.firstName +
                                                                ' ' +
                                                                data.saleOrderShippingAddressModel.lastName}
                                                        </span>
                                                        <span style={{ margin: '0 4px' }}>|</span>
                                                        <span>{data.saleOrderShippingAddressModel.address}</span>
                                                        <span style={{ margin: '0 4px' }}>|</span>
                                                        <span>
                                                            {(
                                                                data.saleOrderShippingAddressModel
                                                                    ?.combinedPhoneNumber ?? ''
                                                            ).trim()}
                                                        </span>
                                                    </div>
                                                    {typeof data.saleOrderShippingAddressModel
                                                        .externalAddressDefinition === 'string' &&
                                                        data.saleOrderShippingAddressModel.externalAddressDefinition.trim() && (
                                                            <p>
                                                                <strong>
                                                                    {t('orderDetail.externalAddressDefinition')}:
                                                                </strong>{' '}
                                                                {
                                                                    data.saleOrderShippingAddressModel
                                                                        .externalAddressDefinition
                                                                }
                                                            </p>
                                                        )}
                                                    {data.saleOrderShippingAddressModel.postalCode && (
                                                        <p>{data.saleOrderShippingAddressModel.postalCode}</p>
                                                    )}
                                                    <p>
                                                        {(
                                                            data.saleOrderShippingAddressModel?.combinedPhoneNumber ??
                                                            ''
                                                        ).trim()}
                                                    </p>
                                                    {Number(data.saleOrderShippingAddressModel?.deliveryMethodId) !==
                                                        DeliveryMethodEnum.PickPoint &&
                                                        data.saleOrderShippingAddressModel.externalReferanceCode && (
                                                            <p>
                                                                {
                                                                    data.saleOrderShippingAddressModel
                                                                        .externalReferanceCode
                                                                }
                                                            </p>
                                                        )}
                                                    <div>{renderSaleOrderShippingAddressDetails()}</div>
                                                </>
                                            )}
                                        </div>
                                    </Grid>
                                </Grid>
                            ) : (
                                <Grid
                                    className={AIC.lockedGrid}
                                    container
                                    style={{ height: '100%' }}
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <div
                                        className={AIC.lockedInner}
                                        style={{
                                            textAlign: 'center',
                                            position: 'relative',
                                            pointerEvents: 'none',
                                        }}
                                    >
                                        <LockIcon
                                            className={AIC.lockedIcon}
                                            style={{
                                                fontSize: 40,
                                                color: 'blue',
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                pointerEvents: 'none',
                                            }}
                                        />
                                        <div
                                            className={AIC.lockedBlurContent}
                                            style={{
                                                filter: 'blur(5px)',
                                                color: 'gray',
                                                userSelect: 'none',
                                            }}
                                        >
                                            <Typography variant="body2" style={{ userSelect: 'none' }}>
                                                {t('general.nameAndSurname')}: Test
                                            </Typography>
                                            <Typography variant="body2" style={{ userSelect: 'none' }}>
                                                {t('general.phone')}: 0555 555 55 55
                                            </Typography>
                                            <Typography variant="body2" style={{ userSelect: 'none' }}>
                                                {t('general.email')}: ornek@lcwaikiki.com
                                            </Typography>
                                        </div>
                                    </div>
                                </Grid>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            )}
        </>
    );
}

export default AddressInformationCard;
